import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SearchIcon from "@mui/icons-material/Search";
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import ApiService from "../../../services/categoryManagementApi";
import { capitalizeFirstLetter } from "../../../utils/helpers";

const { reportsAndAnalyticsApi } = ApiService;

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#a4de6c", "#d0ed57", "#8dd1e1"];

// UTC helpers
function toUTCStart(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}
function toUTCEnd(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

const DemographicReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [search, setSearch] = useState("");

  // Date validation for safe fetch
  const isDateRangeValid =
    fromDate &&
    toDate &&
    !isNaN(new Date(fromDate)) &&
    !isNaN(new Date(toDate)) &&
    toDate >= fromDate;

  const fetchReport = async () => {
    if (!isDateRangeValid) {
      toast.error("Please select a valid date range.");
      return;
    }
    setLoading(true);
    try {
      const response = await reportsAndAnalyticsApi.getUserReport({
        type: "demographics",
        from: toUTCStart(new Date(fromDate)).toISOString(),
        to: toUTCEnd(new Date(toDate)).toISOString(),
        search,
      });
      setReportData(response?.data?.data?.result || {});
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch demographic report");
      console.error("Error fetching demographic report:", error);
    } finally {
      setReportGenerated(true);
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSearch("");
    setReportData(null);
    setReportGenerated(false);
  };

  const exportToExcel = () => {
    const exportData = Object.entries(reportData || {}).map(([key, value]) => ({
      Category: key,
      Value: value,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Demographic Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `DemographicReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const chartData =
    reportData &&
    Object.entries(reportData).map(([key, value]) => ({
      name: capitalizeFirstLetter(key),
      value,
    }));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          Demographic Report
        </Typography>
        <Grid container spacing={2} alignItems="center" mb={2}>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={val => {
                setFromDate(val);
                if (toDate && val && toDate < val) setToDate(null);
              }}
              maxDate={toDate || undefined}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={val => setToDate(val)}
              minDate={fromDate || undefined}
              maxDate={new Date()}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
         
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchReport}
              disabled={loading || !isDateRangeValid}
              sx={{ height: 48 }}
            >
              {loading ? "Loading..." : "Generate Report"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              sx={{ height: 48 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* -- Results section -- */}
        {reportData && Object.keys(reportData).length > 0 ? (
          <>
            <Button variant="contained" onClick={exportToExcel} sx={{ mb: 2 }}>
              Export Excel
            </Button>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(reportData).map(([key, value], idx) => (
                        <TableRow key={idx}>
                          <TableCell>{capitalizeFirstLetter(key)}</TableCell>
                          <TableCell>{value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ height: 400, padding: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        fill="#8884d8"
                        label
                      >
                        {chartData.map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </>
        ) : reportGenerated ? (
          <Typography sx={{ mt: 3 }} color="text.secondary" align="center">
            No data found.
          </Typography>
        ) : (
          // Card placeholder for initial state
          <Paper
            elevation={2}
            sx={{
              mt: 4,
              width: "100%",
              minHeight: "35vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fafbfc",
              boxSizing: "border-box",
              p: { xs: 2, sm: 4 },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Select a date range and click <strong>Generate Report</strong> to see demographic data visualizations and tables.
            </Typography>
           
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DemographicReport;
